import React, { Fragment, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getMyUpcSlips, exportUpcExcel, deleteUpcSlipsAction } from "../../redux/actions/upcScanActions";
import { DELETE_UPC_SLIPS_RESET } from "../../redux/constants/upcScanConstants";
import Loader from "../loader/loader";
import Img2 from "../images/adjust.png";
import { Link } from "react-router-dom";
import useNavHighlight from "../../hooks/useNavHighlight";
import usePageMeta from "../../hooks/usePageMeta";
import SlipsFilterPanel from "./SlipsFilterPanel";
import SlipsTable from "./SlipsTable";
import Pagination from "../shared/Pagination";
import { toast } from "react-toastify";

const sectionKey = (group, type) => `${group.periodKey}:${type}`;

const MyUpcScanSlips = () => {
    const dispatch = useDispatch();
    const { loading, monthlyGroups, totalPages, deleteLoading, deleteMessage, error } = useSelector(state => state.myUpcSlips);
    const { loading: exportLoading } = useSelector(state => state.exportUpcExcel);

    const [page, setPage] = useState(1);
    const [selectedSections, setSelectedSections] = useState([]);
    const [showFilters, setShowFilters] = useState(false);
    const [filterCount, setFilterCount] = useState(0);
    const [status, setstatus] = useState('');
    const [date_before, setDate_before] = useState("");
    const [date_after, setDate_after] = useState("");

    useNavHighlight("UpcScanSlip");
    usePageMeta("upcScans");

    const buildQuery = useCallback(() => {
        const query = {};
        if (status) query.status = status;
        if (date_before) query.date_before = date_before;
        if (date_after) query.date_after = date_after;
        return query;
    }, [status, date_before, date_after]);

    const refreshSlips = useCallback(() => {
        dispatch(getMyUpcSlips(page, buildQuery()));
    }, [dispatch, page, buildQuery]);

    useEffect(() => {
        refreshSlips();
    }, [refreshSlips]);

    useEffect(() => {
        let count = 0;
        if (status) count++;
        if (date_before) count++;
        if (date_after) count++;
        setFilterCount(count);
    }, [status, date_before, date_after]);

    useEffect(() => {
        if (deleteMessage) {
            toast.success(deleteMessage);
            setSelectedSections([]);
            refreshSlips();
            dispatch({ type: DELETE_UPC_SLIPS_RESET });
        }
        if (error) toast.error(error);
    }, [deleteMessage, error, dispatch, refreshSlips]);

    const hasSectionData = (group, type) =>
        type === "valid" ? group.validItems?.length > 0 : group.invalidItems?.length > 0;

    const isSectionSelected = (group, type) =>
        selectedSections.includes(sectionKey(group, type));

    const toggleSection = (group, type) => {
        const key = sectionKey(group, type);
        setSelectedSections((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
        );
    };

    const getSelectableSectionKeys = () =>
        monthlyGroups.flatMap((group) =>
            (["valid", "invalid"])
                .filter((type) => hasSectionData(group, type))
                .map((type) => sectionKey(group, type))
        );

    const selectAllOnPage = () => {
        const keys = getSelectableSectionKeys();
        if (keys.length === 0) return;
        const allSelected = keys.every((key) => selectedSections.includes(key));
        setSelectedSections(allSelected ? [] : keys);
    };

    const clearSelection = () => {
        setSelectedSections([]);
    };

    const getTotalQuantity = (items = []) => {
        return items.reduce((total, item) => total + Number(item.quantity || 0), 0);
    };

    const buildExportPayload = () => {
        return {
            exports: selectedSections.map((key) => {
                const [periodKey, scanType] = key.split(":");
                const group = monthlyGroups.find((g) => g.periodKey === periodKey);
                return {
                    slipIds: group?.slipIds || [],
                    scanType,
                };
            }).filter((entry) => entry.slipIds.length > 0),
        };
    };

    const getSelectedSlipIds = () => {
        const ids = new Set();
        selectedSections.forEach((key) => {
            const periodKey = key.split(":")[0];
            const group = monthlyGroups.find((g) => g.periodKey === periodKey);
            group?.slipIds?.forEach((id) => ids.add(id));
        });
        return [...ids];
    };

    const handleExport = async () => {
        if (selectedSections.length === 0) return;
        await dispatch(exportUpcExcel(buildExportPayload()));
        clearSelection();
        refreshSlips();
    };

    const handleDeleteSelected = () => {
        const slipIds = getSelectedSlipIds();
        if (slipIds.length === 0) return;
        if (!window.confirm(`Delete ${slipIds.length} selected scan slip(s)? This cannot be undone.`)) return;
        dispatch(deleteUpcSlipsAction({ slipIds }));
    };

    const handleDeleteAll = () => {
        if (!window.confirm("Delete ALL scan slips? This cannot be undone.")) return;
        dispatch(deleteUpcSlipsAction({ deleteAll: true }));
    };

    const selectableKeys = getSelectableSectionKeys();
    const allOnPageSelected =
        selectableKeys.length > 0 && selectableKeys.every((key) => selectedSections.includes(key));
    const selectedSlipIds = getSelectedSlipIds();

    return (
        <Fragment>
            {(loading || deleteLoading) && <Loader />}

            <section className="pl-[1.5vmax] pr-[2.5vmax]">
                <div className="flex justify-between items-center bg-white p-[1.2vmax] rounded-[1vmax] px-[2vmax]">
                    <h2 className="text-[1.3vmax] font-bold">UPC Scans</h2>
                    <div className="flex-center gap-[0.8vmax] text-[1vmax]">
                        <Link to={"/"}>Dashboard</Link>
                        <div className="w-[0.3vmax] h-[0.3vmax] rounded-[50%] bg-black"></div>
                        <span className="text-slate-400">UPC Scans</span>
                    </div>
                </div>
            </section>

            <div className="flex justify-end my-5 pr-[2.5vmax]">
                <button
                    className="flex-center font-bold py-[0.6vmax] px-[1.5vmax] bg-[image:var(--gradient)] gap-[1vmax] rounded-[2vmax] text-white relative"
                    onClick={() => setShowFilters(true)}
                >
                    <span className='absolute top-[-0.5vmax] left-0 py-[0.1vmax] px-[0.4vmax] bg-white text-[var(--color-2)] rounded-[50%] shadow-1'>{filterCount}</span>
                    <img src={Img2} alt="" className='w-[1.5vmax]' />
                    <span>Filters</span>
                </button>
            </div>

            {showFilters && (
                <SlipsFilterPanel
                    date_after={date_after}
                    setDate_after={setDate_after}
                    date_before={date_before}
                    setDate_before={setDate_before}
                    status={status}
                    setstatus={setstatus}
                    setPage={setPage}
                    onClose={() => setShowFilters(false)}
                />
            )}

            {monthlyGroups?.length > 0 && (
                <section className="pl-[1.5vmax] pr-[2.5vmax] mb-4 sticky top-0 z-20">
                    <div className="bg-white shadow-1 rounded-2xl p-4 flex flex-wrap items-center gap-4 border border-gray-100">
                        <label className="flex items-center gap-2 cursor-pointer text-[14px] font-semibold text-gray-700">
                            <input
                                type="checkbox"
                                className="w-5 h-5 accent-blue-600"
                                checked={allOnPageSelected}
                                onChange={selectAllOnPage}
                            />
                            Select all on page
                        </label>

                        {selectedSections.length > 0 && (
                            <span className="text-[13px] font-semibold text-blue-600">
                                {selectedSections.length} section{selectedSections.length !== 1 ? "s" : ""} selected
                            </span>
                        )}

                        <div className="flex flex-wrap items-center gap-2 ml-auto">
                            {selectedSections.length > 0 && (
                                <button
                                    type="button"
                                    onClick={clearSelection}
                                    className="px-4 py-2 text-[13px] font-semibold bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg"
                                >
                                    Clear Selection
                                </button>
                            )}

                            <button
                                type="button"
                                onClick={handleExport}
                                disabled={selectedSections.length === 0 || exportLoading}
                                className="px-4 py-2 text-[13px] font-semibold bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                {exportLoading ? "Exporting..." : "Export Selected"}
                            </button>

                            <button
                                type="button"
                                onClick={handleDeleteSelected}
                                disabled={selectedSlipIds.length === 0 || deleteLoading}
                                className="px-4 py-2 text-[13px] font-semibold bg-red-600 hover:bg-red-700 text-white rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                Delete Selected
                            </button>

                            <button
                                type="button"
                                onClick={handleDeleteAll}
                                disabled={deleteLoading}
                                className="px-4 py-2 text-[13px] font-semibold bg-red-100 hover:bg-red-200 text-red-700 rounded-lg disabled:opacity-40"
                            >
                                Delete All
                            </button>
                        </div>
                    </div>
                </section>
            )}

            {selectedSections.length > 0 && (
                <div className="fixed bottom-0 left-0 right-0 z-30 px-4 py-3 bg-white border-t border-gray-200 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
                    <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-between gap-3">
                        <span className="text-[14px] font-semibold text-gray-700">
                            {selectedSections.length} section{selectedSections.length !== 1 ? "s" : ""} selected
                        </span>
                        <div className="flex flex-wrap items-center gap-2">
                            <button
                                type="button"
                                onClick={clearSelection}
                                className="px-4 py-2 text-[13px] font-semibold bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg"
                            >
                                Clear
                            </button>
                            <button
                                type="button"
                                onClick={handleExport}
                                disabled={exportLoading}
                                className="px-4 py-2 text-[13px] font-semibold bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-40"
                            >
                                {exportLoading ? "Exporting..." : "Export Selected"}
                            </button>
                            <button
                                type="button"
                                onClick={handleDeleteSelected}
                                disabled={deleteLoading}
                                className="px-4 py-2 text-[13px] font-semibold bg-red-600 hover:bg-red-700 text-white rounded-lg disabled:opacity-40"
                            >
                                Delete Selected
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <SlipsTable
                monthlyGroups={monthlyGroups}
                isSectionSelected={isSectionSelected}
                toggleSection={toggleSection}
                getTotalQuantity={getTotalQuantity}
            />

            {selectedSections.length > 0 && <div className="h-20" aria-hidden="true" />}

            <Pagination page={page} totalPages={totalPages} setPage={setPage} />

            <br />
            <br />
        </Fragment>
    );
};

export default MyUpcScanSlips;
